import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { Accordion } from '@risksmart-app/atomic-ui';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import { useGetOversightAssessmentRiskAssessmentResultsByRiskId } from 'src/hooks/queries';
import { useIsFeatureFlagEnabled } from 'src/hooks/useIsFeatureFlagEnabled';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { Permission } from 'src/rbac/Permission';

import { getCounter } from '@/utils/collectionUtils';

import AssessmentResultModal from '../../../../assessments/modals/AssessmentResultModal';
import { useGetCollectionTableProps } from './complianceRatingConfig';

interface Props {
  risk: ObjectWithContributors;
}

const ComplianceRatingRegister: FC<Props> = ({ risk }) => {
  useI18NSummaryHelpContent('assessments.tabHelp');
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'ratings' });

  const oversightRatingEntryEnabled = useIsFeatureFlagEnabled(
    'oversight_rating_performance_entry'
  );

  const { data, loading, refetch } =
    useGetOversightAssessmentRiskAssessmentResultsByRiskId({
      queryArgs: { riskId: risk.Id },
    });

  const [isEditOpen, setIsEditOpen] = useState(false);

  const [openAssessmentResultId, setOpenAssessmentResultId] = useState<
    string | undefined
  >();

  const handleAssessmentResultModalClose = async (saved?: boolean) => {
    if (saved) {
      await refetch();
    }
    setOpenAssessmentResultId(undefined);
    setIsEditOpen(false);
  };

  const onAddRating = () => {
    setOpenAssessmentResultId(undefined);
    setIsEditOpen(true);
  };

  const onOpenResult = (id: string) => {
    setOpenAssessmentResultId(id);
    setIsEditOpen(true);
  };

  const controlledRatings =
    data?.risk_controlled_oversight_result.map((item) => ({
      ...item,
      ControlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
    })) ?? [];
  const uncontrolledRatings =
    data?.risk_uncontrolled_oversight_result.map((item) => ({
      ...item,
      ControlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
    })) ?? [];

  const tableProps = useGetCollectionTableProps(risk.Id, onOpenResult, [
    ...controlledRatings,
    ...uncontrolledRatings,
  ]);

  return (
    <>
      <SpaceBetween size={'m'}>
        {oversightRatingEntryEnabled && (
          <div className={'flex justify-end'}>
            <Permission
              permission={'insert:risk_controlled_oversight_result'}
              parentObject={risk}
            >
              <Button
                iconName={'add-plus'}
                variant={'primary'}
                formAction={'none'}
                onClick={onAddRating}
              >
                {t('assessments.add_rating_button')}
              </Button>
            </Permission>
          </div>
        )}
        <Accordion defaultValue={[]}>
          <Accordion.Item value={'compliance-ratings'}>
            <Accordion.Header>
              <Accordion.Trigger>
                <div className={'flex space-x-2'}>
                  <span>{st('complianceRatingSubheading')}</span>
                  <span className={'text-grey font-normal'}>
                    {getCounter(tableProps.totalItemsCount ?? 0, loading)}
                  </span>
                </div>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content>
              <Table
                {...tableProps}
                loading={loading}
                variant={'embedded'}
                data-testid={'compliance-assessment-rating-table'}
              />
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      </SpaceBetween>
      {isEditOpen && (
        <AssessmentResultModal
          id={openAssessmentResultId}
          onDismiss={handleAssessmentResultModalClose}
          assessedItem={risk}
          resultType={Parent_Type_Enum.RiskAssessmentResult}
          hideTypeSelector={true}
          i18n={t('ratings')}
          assessmentMode={'oversight_assessment'}
        />
      )}
    </>
  );
};
export default ComplianceRatingRegister;
